import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import MarkdownRenderer from './MarkdownRenderer';
import './Chat.css';

interface Message {
  role: 'user' | 'bot';
  text: string;
  usageGpt?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  modelGpt?: string;
  replyGemini?: string;
  usageGemini?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  modelGemini?: string;
}

const getApiUrl = () => {
  return window.location.hostname === 'localhost'
    ? 'http://localhost/api/crm'
    : '/api/crm';
};

function Chat() {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'bot', text: '안녕하세요. ○○회사 업무 지원 AI입니다. 규정, 프로세스, 시스템 관련 질문을 입력해 주세요.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-4o-2024-11-20');
  const [activeTabByIndex, setActiveTabByIndex] = useState<{ [key: number]: 'gpt' | 'gemini' }>({});
  const [useTyping, setUseTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false); // ✅ 추가
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const isSendingRef = useRef(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;

    const handleScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
      setShowScrollButton(!nearBottom);
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, typingText]);

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      setShowScrollButton(false);
    }
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 500); // 1초 후 사라짐
  };

  const handleSend = async () => {
    if (isSendingRef.current || !input.trim()) return;
    isSendingRef.current = true;

    const userMessage: Message = { role: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setTypingText('');

    try {
      const res = await axios.post(getApiUrl(), {
        messages: messages.map(m => ({
          role: m.role === 'bot' ? 'assistant' : m.role,
          content: m.text
        })).concat({ role: 'user', content: input }),
        model: selectedModel
      });

      const { replyGpt, usageGpt, modelGpt, replyGemini, usageGemini, modelGemini } = res.data;
      const index = messages.length + 1;

      if (!useTyping) {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: replyGpt,
          usageGpt,
          modelGpt,
          replyGemini,
          usageGemini,
          modelGemini
        }]);
        setActiveTabByIndex(prev => ({ ...prev, [index]: 'gpt' }));
        setLoading(false);
        isSendingRef.current = false;
      } else {
        let i = 0;
        let accumulated = '';
        const interval = setInterval(() => {
          if (i >= replyGpt.length) {
            clearInterval(interval);
            setTypingText('');
            setMessages(prev => [...prev, {
              role: 'bot',
              text: replyGpt,
              usageGpt,
              modelGpt,
              replyGemini,
              usageGemini,
              modelGemini
            }]);
            setActiveTabByIndex(prev => ({ ...prev, [index]: 'gpt' }));
            setLoading(false);
            isSendingRef.current = false;
            return;
          }
          accumulated += replyGpt[i];
          setTypingText(accumulated);
          i++;
        }, 1);
      }

    } catch (err) {
      setMessages(prev => [...prev, { role: 'bot', text: '서버 에러가 발생했어요 😢' }]);
      setLoading(false);
      isSendingRef.current = false;
    }
  };

  return (
    <div className={`chat-container ${isDarkMode ? 'dark' : ''}`}>


    {/* ✅ 토스트 메시지 딤처리 오버레이 */}
    {toastMessage && (
      <div className="toast-overlay">
        <div className="toast-message">{toastMessage}</div>
      </div>
    )}

    
      <div className="chat-header">
        <div className="logo"></div>
        <div className="title-wrapper">
          <span className="title-main">기업용 AI</span>
          {/* <select
            value={selectedModel}
            onChange={e => setSelectedModel(e.target.value)}
            className="model-select"
          >
            <option value="gpt-3.5-turbo">3.5.Turbo</option>
            <option value="gpt-4-turbo">4.Turbo</option>
            <option value="gpt-4o-2024-11-20">4.o</option>
          </select> */}

          {/* 🌙 다크모드 아이콘 */}
          <button
            className="icon-toggle"
            onClick={() => {
              setIsDarkMode(prev => {
                showToast(prev ? '☀️ 라이트 모드로 전환' : '🌙 다크 모드로 전환');
                return !prev;
              });
            }}
          >
            {isDarkMode ? '🌞' : '🌙'}
          </button>

          {/* ⌨️ 타이핑 효과 아이콘 */}
          {/* <button
            className="icon-toggle"
            onClick={() => {
              setUseTyping(prev => {
                showToast(prev ? '✏️ 타이핑 효과 해제' : '⌨️ 타이핑 효과 설정');
                return !prev;
              });
            }}
          >
            {useTyping ? '⌨️' : '✏️'}
          </button> */}
        </div>

        <button className="reset-button" onClick={() => window.location.reload()}>
        🔄
        </button>
      </div>

      <div className="chat-box" ref={chatBoxRef}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`chat-bubble ${msg.role}`}>
            {msg.role === 'user' && <MarkdownRenderer content={msg.text} />}
            {msg.role === 'bot' && (
              <>
                {(msg.replyGemini && msg.modelGemini) && (
                  <div className="inner-tab-buttons">
                    <button
                      className={activeTabByIndex[idx] === 'gpt' ? 'active' : ''}
                      onClick={() => setActiveTabByIndex(prev => ({ ...prev, [idx]: 'gpt' }))}
                    >GPT</button>
                    <button
                      className={activeTabByIndex[idx] === 'gemini' ? 'active' : ''}
                      onClick={() => setActiveTabByIndex(prev => ({ ...prev, [idx]: 'gemini' }))}
                    >Gemini</button>
                  </div>
                )}
                {(activeTabByIndex[idx] === 'gpt' || !msg.replyGemini) && (
                  <>
                    {msg.modelGpt && (
                      <div className="model-name">
                        <strong>@기업용AI</strong>
                      </div>
                    )}
                    <MarkdownRenderer content={msg.text} />
                    {/* {msg.usageGpt && (
                      <div className="usage">
                        <small>(토큰 사용량: {msg.usageGpt.total_tokens}개)</small>
                      </div>
                    )} */}
                  </>
                )}
                {activeTabByIndex[idx] === 'gemini' && msg.replyGemini && (
                  <>
                    <div className="model-name">
                      {/* 🌟 <strong>{msg.modelGemini}</strong>의 답변: */}
                      🌟 <strong>{msg.modelGemini}</strong>의 답변:
                    </div>
                    <MarkdownRenderer content={msg.replyGemini} />
                    {/* {msg.usageGemini && (
                      <div className="usage">
                        <small>(토큰 사용량: {msg.usageGemini.total_tokens}개)</small>
                      </div>
                    )} */}
                  </>
                )}
              </>
            )}
          </div>
        ))}

        {loading && (
          <div className="chat-bubble bot">
            {useTyping && typingText ? (
              <>
                <MarkdownRenderer content={typingText} />
                <span className="typing-cursor">|</span>
              </>
            ) : (
              <div className="typing-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
          </div>
        )}

        {showScrollButton && (
          <button className="scroll-to-bottom-btn" onClick={scrollToBottom}>
            ⬇︎
          </button>
        )}
      </div>

      <div className="chat-input">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="메시지를 입력하세요..."
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button onClick={handleSend} disabled={loading}>➤</button>
      </div>

      <div className="chat-footer">
        🤖BackendLab © 2025 All rights reserved
      </div>
    </div>
  );
}



export default Chat;
